import { DragEvent, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../providers/authProvider";
import { useData } from "../providers/dataProvider";
import { CONTEXTS, Context as TodoContext, MAX_REPEAT_EVERY, RepeatFrom, RepeatUnit, TodoData, TodoRepeat } from "../../../types";
import TitleInput from "./TitleInput";
import { FeedbackWidget } from "./FeedbackWidget";
import { AdminView, useIsAdmin } from "./AdminView";
import { FontSizeControl } from "./FontSizeControl";
import { SharedWithMe } from "./SharedWithMe";
import { WhatsNew } from "./WhatsNew";
import { ShareControls } from "./ShareControls";
import { MotionControl } from "./MotionControl";
import { CompleteCheckbox } from "./CompleteCheckbox";
import { ShortcutsHelp } from "./ShortcutsHelp";
import { useFocusTrap } from "../useFocusTrap";
import { handleGlobalKeydown, isDialogOpen } from "../shortcuts";
import { nextStepOrder } from "../providers/stepOrder";
import { dueFirst, formatDueDate, isDueSoon, ymdOf } from "../dueDate";
import { describeRepeat, formatRepeatShort, parseRepeatDraft, repeatFrom, sameRepeat } from "../repeat";

const getTodayLocal = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const CAPTURE_CONTEXT_KEY = "todoit-capture-context";

const getStoredCaptureContext = (): TodoContext => {
  try {
    const stored = window.localStorage.getItem(CAPTURE_CONTEXT_KEY);
    if (stored === CONTEXTS.WORK || stored === CONTEXTS.HOME) return stored;
  } catch {}
  return CONTEXTS.WORK;
};

const COLLAPSED_PARENTS_KEY = "todoit-collapsed-parents";

const getStoredCollapsedParents = (): Record<string, boolean> => {
  try {
    const stored = window.localStorage.getItem(COLLAPSED_PARENTS_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return {};
};

const rank = (n?: number) => n ?? Infinity;

// Today-section sort. Kept as a plain key -> comparator map so another mode
// (e.g. a manual/drag order) is just one more entry, and rolling back is
// deleting this block, the selector, and the one `.sort` call in todayTodos.
const TODAY_SORTS = {
  manual: {
    label: "Manual (drag to reorder)",
    compare: (a: TodoData, b: TodoData) => a.order - b.order,
  },
  created: {
    label: "Created",
    compare: (a: TodoData, b: TodoData) => b.createdAt.localeCompare(a.createdAt),
  },
  priority: {
    label: "Priority",
    // P1 first, unprioritised last, ties fall back to newest first.
    compare: (a: TodoData, b: TodoData) =>
      (a.priority ?? Infinity) - (b.priority ?? Infinity) ||
      b.createdAt.localeCompare(a.createdAt),
  },
  prioritySize: {
    label: "Priority, then size",
    compare: (a: TodoData, b: TodoData) =>
      rank(a.priority) - rank(b.priority) ||
      rank(a.complexity) - rank(b.complexity) ||
      b.createdAt.localeCompare(a.createdAt),
  },
  sizePriority: {
    label: "Size, then priority",
    compare: (a: TodoData, b: TodoData) =>
      rank(a.complexity) - rank(b.complexity) ||
      rank(a.priority) - rank(b.priority) ||
      b.createdAt.localeCompare(a.createdAt),
  },
};
type TodaySort = keyof typeof TODAY_SORTS;
const DEFAULT_TODAY_SORT: TodaySort = "created";
const TODAY_SORT_KEY = "todoit-today-sort";

const getStoredTodaySort = (): TodaySort => {
  try {
    const stored = window.localStorage.getItem(TODAY_SORT_KEY);
    if (stored && stored in TODAY_SORTS) return stored as TodaySort;
  } catch {}
  return DEFAULT_TODAY_SORT;
};

// Project-step sort. "manual" keeps the stored order (creation order for new
// and moved-in steps, whose `order` is seeded from the current time so they land
// last; legacy steps have negative orders and still sort first, newest-first); the others sort by priority (P1
// first) and size (S first) in either precedence, unset last, remaining ties
// keeping that stored order. The key stays "manual" so saved preferences
// still resolve; only the label says what it actually is.
const STEP_SORTS = {
  manual: {
    label: "Creation order",
    compare: (a: TodoData, b: TodoData) => a.order - b.order,
  },
  prioritySize: {
    label: "Priority, then size",
    compare: (a: TodoData, b: TodoData) =>
      rank(a.priority) - rank(b.priority) ||
      rank(a.complexity) - rank(b.complexity) ||
      a.order - b.order,
  },
  sizePriority: {
    label: "Size, then priority",
    compare: (a: TodoData, b: TodoData) =>
      rank(a.complexity) - rank(b.complexity) ||
      rank(a.priority) - rank(b.priority) ||
      a.order - b.order,
  },
};
type StepSort = keyof typeof STEP_SORTS;
const STEP_SORT_KEY = "todoit-step-sort";

const getStoredStepSort = (): StepSort => {
  try {
    const stored = window.localStorage.getItem(STEP_SORT_KEY);
    if (stored && stored in STEP_SORTS) return stored as StepSort;
  } catch {}
  return "manual";
};

// Where a dragged item lands: it takes the target's slot (after it when
// moving down, before it when moving up), and gets an `order` halfway
// between its new neighbours so nothing else has to be rewritten. Returns
// null for a no-op or an item that isn't in this list.
const orderForDrop = (list: TodoData[], draggedId: string, targetId: string): number | null => {
  const from = list.findIndex((t) => t.id === draggedId);
  const to = list.findIndex((t) => t.id === targetId);
  if (from === -1 || to === -1 || from === to) return null;
  const rest = list.filter((t) => t.id !== draggedId);
  const targetIdx = rest.findIndex((t) => t.id === targetId);
  const at = from < to ? targetIdx + 1 : targetIdx;
  const prev = rest[at - 1];
  const next = rest[at];
  if (prev && next) return (prev.order + next.order) / 2;
  if (next) return next.order - 1;
  return prev.order + 1;
};

// Drag-and-drop reordering within one displayed list. Only the handle is
// draggable (so text selection/editing inside the row keeps working); rows
// are the drop targets.
const useReorder = (
  todos: TodoData[],
  onReorder?: (todo: TodoData, order: number) => void,
) => {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const end = () => {
    setDragId(null);
    setOverId(null);
  };
  return {
    enabled: !!onReorder && todos.length > 1,
    dragId,
    overId,
    handleProps: (todo: TodoData) => ({
      draggable: true,
      onDragStart: (e: DragEvent<HTMLElement>) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", todo.id);
        const row = e.currentTarget.closest("li");
        if (row) e.dataTransfer.setDragImage(row, 0, 0);
        setDragId(todo.id);
      },
      onDragEnd: end,
    }),
    rowProps: (todo: TodoData) => ({
      onDragOver: (e: DragEvent<HTMLElement>) => {
        if (!dragId) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (overId !== todo.id) setOverId(todo.id);
      },
      onDrop: (e: DragEvent<HTMLElement>) => {
        e.preventDefault();
        const dragged = todos.find((t) => t.id === dragId);
        const order = dragId ? orderForDrop(todos, dragId, todo.id) : null;
        if (dragged && order !== null) onReorder?.(dragged, order);
        end();
      },
    }),
  };
};

const DragHandle = (props: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    {...props}
    title="Drag to reorder"
    aria-label="Drag to reorder"
    className="shrink-0 cursor-grab select-none text-neutral-300 hover:text-neutral-500"
  >
    ⠿
  </span>
);

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const byDoneAtDesc = (a: TodoData, b: TodoData) => (b.doneAt || "").localeCompare(a.doneAt || "");

const matchesQuery = (todo: TodoData, words: string[]) => {
  const haystack = `${todo.title} ${todo.notes || ""}`.toLowerCase();
  return words.every((w) => haystack.includes(w));
};

export const Dashboard = () => {
  const { logout } = useAuth();
  const { todos, loading, createTodo, updateTodo, deleteTodo } = useData();
  const [contextFilter, setContextFilter] = useState<TodoContext | "all">("all");
  const [captureContext, setCaptureContext] = useState<TodoContext>(getStoredCaptureContext);
  const [title, setTitle] = useState("");
  const [childDrafts, setChildDrafts] = useState<Record<string, string>>({});
  const [addingChildTo, setAddingChildTo] = useState<string | null>(null);
  const [collapsedParents, setCollapsedParents] = useState<Record<string, boolean>>(
    getStoredCollapsedParents,
  );
  const [expandedDoneChildren, setExpandedDoneChildren] = useState<Record<string, boolean>>({});
  const [expandedCompletedChildren, setExpandedCompletedChildren] = useState<
    Record<string, boolean>
  >({});
  const [showCompleted, setShowCompleted] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [todaySort, setTodaySort] = useState<TodaySort>(getStoredTodaySort);
  const [stepSort, setStepSort] = useState<StepSort>(getStoredStepSort);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isAdmin = useIsAdmin();
  const [view, setView] = useState<"todos" | "admin">("todos");
  const showAdmin = isAdmin && view === "admin";
  const [showShortcuts, setShowShortcuts] = useState(false);
  const today = getTodayLocal();

  // Global keys (see ../shortcuts.ts): "/" focuses search from anywhere unless
  // already typing somewhere else (and not in the Admin view, where the todo
  // view is hidden); Escape clears search when it's focused; "?" opens the
  // shortcuts list unless a modal or a popover (settings, todo menu) is already up.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) =>
      handleGlobalKeydown(e, {
        showAdmin,
        modalOpen: () => isDialogOpen(document),
        searchFocused: () => document.activeElement === searchInputRef.current,
        focusSearch: () => searchInputRef.current?.focus(),
        clearSearch: () => {
          setSearchQuery("");
          searchInputRef.current?.blur();
        },
        openShortcuts: () => setShowShortcuts(true),
      });
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showAdmin]);

  const toggleCollapsed = (id: string) =>
    setCollapsedParents((c) => {
      const next = { ...c, [id]: !c[id] };
      try {
        window.localStorage.setItem(COLLAPSED_PARENTS_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  const toggleDoneChildrenVisible = (id: string) =>
    setExpandedDoneChildren((c) => ({ ...c, [id]: !c[id] }));
  const toggleCompletedChildrenVisible = (id: string) =>
    setExpandedCompletedChildren((c) => ({ ...c, [id]: !c[id] }));

  const setCaptureContextPersisted = (context: TodoContext) => {
    setCaptureContext(context);
    try {
      window.localStorage.setItem(CAPTURE_CONTEXT_KEY, context);
    } catch {}
  };

  const visibleTodos = useMemo(
    () =>
      contextFilter === "all"
        ? todos
        : todos.filter((t) => t.context === contextFilter),
    [todos, contextFilter],
  );

  // A todo with children acts as a project; one with none is a plain leaf.
  // Nesting is capped at one level, enforced only here in the UI (a todo
  // that already has children is never offered as something to nest
  // further, and the picker for "which parent" only exists at top level).
  // childrenOf is what a tab renders, so it respects the context filter.
  const childrenOf = (parentId: string) =>
    visibleTodos.filter((t) => t.parentId === parentId).sort((a, b) => a.order - b.order);

  // A parent can live in a different context than its child (e.g. a home
  // project with a work step). Under a context filter the parent is still
  // kept when it has a visible child, otherwise that child would have no
  // section to appear in and vanish from the view.
  const topLevelTodos = useMemo(() => {
    const visibleParentIds = new Set(visibleTodos.map((t) => t.parentId));
    return todos.filter(
      (t) =>
        !t.parentId &&
        (contextFilter === "all" || t.context === contextFilter || visibleParentIds.has(t.id)),
    );
  }, [todos, visibleTodos, contextFilter]);

  // Whether a todo is a project is a fact about the todo, not the tab: it's
  // checked against every todo regardless of the context filter. Otherwise
  // a work parent whose only steps are home would look childless on the
  // work tab, land in the Inbox as a plain leaf, and even be offered
  // "move to…" — nesting a project under another todo, which the one-level
  // cap is meant to rule out.
  //
  // One pass over `todos` builds the set of ids that have a child, so each
  // hasChildren call is an O(1) lookup instead of a scan — and the memos
  // below can depend on exactly what they read, rather than on `todos` as a
  // stand-in.
  const parentIds = useMemo(() => new Set(todos.map((t) => t.parentId)), [todos]);
  const hasChildren = (id: string) => parentIds.has(id);

  // Open steps a tab hides because they live in the other context, so the
  // project section can say so instead of claiming it's empty.
  const hiddenOpenStepsOf = (parentId: string) =>
    contextFilter === "all"
      ? 0
      : todos.filter((c) => c.parentId === parentId && !c.done && c.context !== contextFilter)
          .length;
  const otherContext = contextFilter === CONTEXTS.WORK ? CONTEXTS.HOME : CONTEXTS.WORK;

  // Reparent targets: any other open, top-level todo — a project (has
  // children already) or a plain leaf alike, per the feedback wording
  // ("a parent or another todo"). The thing being reparented must itself be
  // childless (one-level nesting), which callers guarantee by only offering
  // this from the Inbox, where every row already meets that bar.
  const eligibleParentsFor = (todo: TodoData) =>
    topLevelTodos.filter((t) => !t.done && t.id !== todo.id);

  // Where an existing step can move to: any other open top-level todo,
  // excluding the parent it's already under. (The Inbox is offered by the
  // menu itself, since it isn't a todo.)
  const moveTargetsFor = (todo: TodoData) =>
    topLevelTodos.filter((t) => !t.done && t.id !== todo.parentId && t.id !== todo.id);

  // Active project sections — a project drops out of this the moment it's
  // itself marked done (however that happened: manually, or by "work on
  // this today" falling back to scheduling the parent once every step was
  // done), and moves to Completed below instead of sitting there empty with
  // no way to close it out.
  const parents = useMemo(
    () => topLevelTodos.filter((t) => !t.done && hasChildren(t.id)),
    [topLevelTodos, parentIds],
  );

  const inboxTodos = useMemo(
    () =>
      topLevelTodos
        .filter((t) => !t.done && !t.scheduledDate && !hasChildren(t.id))
        .sort((a, b) => a.order - b.order),
    [topLevelTodos, parentIds],
  );

  // Completed, top-level only — any done top-level todo, project or plain
  // leaf alike. A done child of a still-active project stays inside that
  // project's own collapsed "completed" sublist instead of duplicating here.
  const completedTopLevel = useMemo(
    () => topLevelTodos.filter((t) => t.done).sort(byDoneAtDesc),
    [topLevelTodos],
  );

  const todayTodos = useMemo(
    () =>
      visibleTodos
        .filter((t) => !t.done && t.scheduledDate && t.scheduledDate <= today)
        .sort(
          todaySort === DEFAULT_TODAY_SORT
            ? dueFirst(TODAY_SORTS[todaySort].compare, today)
            : TODAY_SORTS[todaySort].compare,
        ),
    [visibleTodos, today, todaySort],
  );

  const reorderTodo = (todo: TodoData, order: number) => updateTodo(todo, { order });
  const inboxReorder = useReorder(inboxTodos, reorderTodo);

  const setStepSortPersisted = (sort: StepSort) => {
    setStepSort(sort);
    try {
      window.localStorage.setItem(STEP_SORT_KEY, sort);
    } catch {}
  };

  const setTodaySortPersisted = (sort: TodaySort) => {
    setTodaySort(sort);
    try {
      window.localStorage.setItem(TODAY_SORT_KEY, sort);
    } catch {}
  };

  // Deferred 2+ times (scheduled/rescheduled without finishing) and not
  // already sitting in Today (which shows its own "deferred N×" badge
  // instead of duplicating the row here).
  const neededAttention = useMemo(
    () =>
      visibleTodos
        .filter(
          (t) =>
            !t.done &&
            t.timesScheduled >= 2 &&
            !(t.scheduledDate && t.scheduledDate <= today),
        )
        .sort((a, b) => b.timesScheduled - a.timesScheduled),
    [visibleTodos, today],
  );

  // Search ignores the context filter (it's meant to find anything,
  // anywhere) — a context tag on each row makes up for that.
  const searchResults = useMemo(() => {
    const words = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return [];
    const matches = todos.filter((t) => matchesQuery(t, words));
    const open = matches.filter((t) => !t.done).sort((a, b) => a.order - b.order);
    const done = matches.filter((t) => t.done).sort(byDoneAtDesc);
    return [...open, ...done];
  }, [todos, searchQuery]);

  // On the "all" tab you pick a context per capture (and it's remembered);
  // on the work/home tab there's nothing to pick — it's just that context.
  const capture = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    const context = contextFilter === "all" ? captureContext : contextFilter;
    await createTodo({ title: title.trim(), context });
    setTitle("");
  };

  // Also used to "bump" an overdue rolled-over item back to literal today —
  // TodoList shows this same action either way, just labeled differently.
  // Default step sort floats due-soon steps to the top; other sorts are untouched.
  const stepCompare =
    stepSort === "manual"
      ? dueFirst(STEP_SORTS[stepSort].compare, today)
      : STEP_SORTS[stepSort].compare;
  const setDueDate = (todo: TodoData, dueDate: string | null) => updateTodo(todo, { dueDate });

  // Moves one todo between work and home. Deliberately just the one: a
  // project's steps keep their own context (mixed-context projects are a
  // supported shape, see topLevelTodos), so each step is switched on its own.
  const setContext = (todo: TodoData, context: TodoContext) => updateTodo(todo, { context });

  // null turns repeating off. Offered on single todos and steps, not on
  // project headers: a reset unchecks only the todo itself, never its steps.
  const setRepeat = (todo: TodoData, repeat: TodoRepeat | null) => updateTodo(todo, { repeat });

  const otherContextOf = (context: TodoContext) =>
    context === CONTEXTS.WORK ? CONTEXTS.HOME : CONTEXTS.WORK;

  // Every step of a project, done or not, on any tab (unlike childrenOf,
  // which only sees the current tab). These are all the user's own todos —
  // useData is the owner's list, shared projects render elsewhere — so each
  // one is editable here. Grouped once per todos change rather than filtered
  // per project: moveStepsTargetFor runs for every rendered project header
  // and Completed row, so a filter there would cost projects × todos a render.
  // Each parent's steps keep their `todos` order (one forward pass).
  const stepsByParent = useMemo(() => {
    const map = new Map<string, TodoData[]>();
    for (const t of todos) {
      if (!t.parentId) continue;
      const steps = map.get(t.parentId);
      if (steps) steps.push(t);
      else map.set(t.parentId, [t]);
    }
    return map;
  }, [todos]);
  const stepsOf = (parent: TodoData) => stepsByParent.get(parent.id) ?? [];

  // Where "Move all steps" would send a project's steps: into the project's
  // own context when any step is elsewhere (the switch-the-project-then-
  // follow flow), otherwise flipped to the other one. Undefined when the
  // todo has no steps, which hides the action.
  const moveStepsTargetFor = (parent: TodoData): TodoContext | undefined => {
    const steps = stepsOf(parent);
    if (!steps.length) return undefined;
    return steps.some((s) => s.context !== parent.context)
      ? parent.context
      : otherContextOf(parent.context);
  };

  // Projects whose "Move all steps" is still running. The ref is the
  // re-entry guard (synchronous, so two clicks in the same tick can't start
  // overlapping runs); the state mirrors it for rendering, so the project's
  // menu can show the run is in flight instead of swallowing the click
  // silently. Copied on every change — more than one project can be mid-run.
  const movingStepsRef = useRef(new Set<string>());
  const [movingSteps, setMovingSteps] = useState<ReadonlySet<string>>(() => new Set());

  // Switches each of the project's steps to `context`, one write at a time
  // (bounded by the project's step count, no retries), skipping steps already
  // there. Stops at the first failure: the provider has already undone that
  // step and shown its banner, earlier steps stay moved — which is exactly
  // what the server now holds. Works from a snapshot of the steps; updateTodo
  // only needs each step's id and createdAt, so a re-render mid-run is fine.
  const moveAllSteps = async (parent: TodoData, context: TodoContext) => {
    if (movingStepsRef.current.has(parent.id)) return;
    movingStepsRef.current.add(parent.id);
    setMovingSteps((s) => new Set(s).add(parent.id));
    try {
      for (const step of stepsOf(parent)) {
        if (step.context === context) continue;
        const ok = await updateTodo(step, { context });
        if (!ok) return;
      }
    } finally {
      movingStepsRef.current.delete(parent.id);
      setMovingSteps((s) => {
        const next = new Set(s);
        next.delete(parent.id);
        return next;
      });
    }
  };

  const scheduleToday = (todo: TodoData) => {
    updateTodo(todo, { scheduledDate: today });
  };

  // Pulling an item back out of Today without finishing it is the actual
  // "deferral" — that's what counts toward the deferred badge, not adding it.
  const removeFromToday = (todo: TodoData) => {
    updateTodo(todo, {
      scheduledDate: null,
      timesScheduled: todo.timesScheduled + 1,
    });
  };

  const resetDeferred = (todo: TodoData) => {
    updateTodo(todo, { timesScheduled: 0 });
  };

  const getParentTitle = (todo: TodoData) =>
    todo.parentId ? todos.find((t) => t.id === todo.parentId)?.title : undefined;

  const getContextTag = (todo: TodoData) => todo.context;

  const editTodo = (
    todo: TodoData,
    updates: { title: string; priority: number | null; complexity: number | null },
  ) => {
    updateTodo(todo, updates);
  };

  // The parent's createdAt goes along so the server can read it by key and
  // check it still exists and is top-level (same as reparentTodo below).
  const addChild = async (parent: TodoData, e: FormEvent) => {
    e.preventDefault();
    const text = (childDrafts[parent.id] || "").trim();
    if (!text) return;
    await createTodo({
      title: text,
      context: parent.context,
      parentId: parent.id,
      parentCreatedAt: parent.createdAt,
    });
    setChildDrafts((d) => ({ ...d, [parent.id]: "" }));
  };

  // Reparents an existing todo under another — puts it at the bottom of the
  // target's children (nextStepOrder()), matching newly added steps, so it
  // doesn't land in some arbitrary spot based on when it was originally created.
  // A null parent un-nests a step into the Inbox, at the top like a freshly
  // captured todo (top-level order is -now, newest first; see createTodo).
  // A real parent is sent with its createdAt so the server can check it still
  // exists and is top-level; the menu only ever offers loaded top-level todos,
  // so a target that isn't in the list is nothing to move to.
  const reparentTodo = (todo: TodoData, parentId: string | null) => {
    if (parentId === null) {
      updateTodo(todo, { parentId: null, order: -Date.now() });
      return;
    }
    const parent = todos.find((t) => t.id === parentId);
    if (!parent) return;
    updateTodo(todo, { parentId, parentCreatedAt: parent.createdAt, order: nextStepOrder() });
  };

  // "Work on this today": the top open, unscheduled direct child — or, if
  // there are none left, the parent itself (per the "no open steps left"
  // case — scheduling the parent is a reasonable default over blocking).
  const scheduleTopOf = (parent: TodoData) => {
    const openChildren = childrenOf(parent.id)
      .filter((c) => !c.done && !c.scheduledDate)
      .sort(stepCompare);
    const target = openChildren[0] ?? parent;
    updateTodo(target, { scheduledDate: today });
  };

  const backgroundClass =
    contextFilter === CONTEXTS.WORK
      ? "bg-sky-50"
      : contextFilter === CONTEXTS.HOME
        ? "bg-amber-50"
        : "bg-white";

  const searching = searchQuery.trim().length > 0;

  return (
    <div className={`min-h-screen transition-colors ${backgroundClass}`}>
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-8">
      <header className="flex flex-wrap items-center justify-between gap-y-2 gap-x-4">
        <div className="flex items-baseline gap-2">
          <button
            onClick={() => {
              setContextFilter("all");
              setSearchQuery("");
            }}
            className="text-xl font-semibold"
          >
            todoit
          </button>
          <span className="text-xs text-neutral-400">v{__APP_VERSION__}</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:gap-y-2">
          {(["all", CONTEXTS.WORK, CONTEXTS.HOME] as const).map((c) => (
            <button
              key={c}
              onClick={() => setContextFilter(c)}
              className={`px-3 py-2.5 sm:py-1 rounded text-sm capitalize ${
                contextFilter === c
                  ? "bg-neutral-900 text-white"
                  : "bg-neutral-100 text-neutral-700"
              }`}
            >
              {c}
            </button>
          ))}
          <HeaderSettings onShowShortcuts={() => setShowShortcuts(true)} />
          {/* Admin-only until prod has release notes; non-admins would see an empty modal. */}
          {isAdmin && <WhatsNew />}
          {isAdmin && (
            <button
              onClick={() => setView(showAdmin ? "todos" : "admin")}
              className="min-h-10 sm:min-h-0 text-sm text-neutral-500 sm:ml-2"
            >
              {showAdmin ? "Todos" : "Admin"}
            </button>
          )}
          <button onClick={logout} className="min-h-10 sm:min-h-0 text-sm text-neutral-500 sm:ml-2">
            Log out
          </button>
        </div>
      </header>
      {showShortcuts && <ShortcutsHelp onClose={() => setShowShortcuts(false)} />}

      {showAdmin && <AdminView />}
      <div hidden={showAdmin} className="space-y-8">
      <SharedWithMe />

      <div className="relative">
        <input
          ref={searchInputRef}
          className="w-full border rounded px-3 py-2 text-sm"
          placeholder="Search everything… (press / to focus)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searching && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-neutral-400"
          >
            clear
          </button>
        )}
      </div>

      <form onSubmit={capture} className="flex gap-2">
        <TitleInput
          className="border rounded px-3 py-2"
          placeholder="Throw something in…"
          value={title}
          onChange={setTitle}
        />
        {contextFilter === "all" && (
          <div className="flex shrink-0 rounded overflow-hidden border">
            {([CONTEXTS.WORK, CONTEXTS.HOME] as const).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCaptureContextPersisted(c)}
                className={`min-h-10 sm:min-h-0 px-2 py-1 text-xs capitalize ${
                  captureContext === c
                    ? "bg-neutral-900 text-white"
                    : "bg-neutral-100 text-neutral-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}
        <button className="shrink-0 bg-neutral-900 text-white rounded px-4">Add</button>
      </form>

      {loading ? (
        <p className="text-neutral-500">Loading…</p>
      ) : searching ? (
        <section>
          <h2 className="text-sm font-semibold text-neutral-500 mb-2">
            Search results ({searchResults.length})
          </h2>
          <TodoList
            todos={searchResults}
            onToggle={(t) => updateTodo(t, { done: !t.done })}
            onDelete={deleteTodo}
            onSchedule={scheduleToday}
            onUnschedule={removeFromToday}
            onSetDue={setDueDate}
            onSetContext={setContext}
            onSetRepeat={setRepeat}
            hasSteps={(t) => hasChildren(t.id)}
            onEdit={editTodo}
            getParentTitle={getParentTitle}
            getContextTag={getContextTag}
            today={today}
            empty="No matches."
          />
        </section>
      ) : (
        <>
          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-neutral-500">Today</h2>
              <label className="text-xs text-neutral-400">
                Sort:{" "}
                <select
                  value={todaySort}
                  onChange={(e) => setTodaySortPersisted(e.target.value as TodaySort)}
                  className="bg-transparent border-none"
                >
                  {(Object.keys(TODAY_SORTS) as TodaySort[]).map((key) => (
                    <option key={key} value={key}>
                      {TODAY_SORTS[key].label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <TodoList
              todos={todayTodos}
              onToggle={(t) => updateTodo(t, { done: !t.done })}
              onDelete={deleteTodo}
              onSchedule={scheduleToday}
              onUnschedule={removeFromToday}
              onSetDue={setDueDate}
              onSetContext={setContext}
              onSetRepeat={setRepeat}
              hasSteps={(t) => hasChildren(t.id)}
              onEdit={editTodo}
              onReset={resetDeferred}
              onReorder={todaySort === "manual" ? reorderTodo : undefined}
              getParentTitle={getParentTitle}
              today={today}
              empty="Nothing scheduled yet."
            />
          </section>

          {neededAttention.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-neutral-500 mb-2">
                Needs attention ({neededAttention.length})
              </h2>
              <TodoList
                todos={neededAttention}
                onToggle={(t) => updateTodo(t, { done: !t.done })}
                onDelete={deleteTodo}
                onSchedule={scheduleToday}
                onSetContext={setContext}
                onSetRepeat={setRepeat}
                hasSteps={(t) => hasChildren(t.id)}
                onEdit={editTodo}
                onReset={resetDeferred}
                getParentTitle={getParentTitle}
                today={today}
                empty=""
              />
            </section>
          )}

          <section>
            <h2 className="text-sm font-semibold text-neutral-500 mb-2">Inbox</h2>
            {!inboxTodos.length ? (
              <p className="text-sm text-neutral-400">Inbox is empty.</p>
            ) : (
              <ul className="space-y-1">
                {inboxTodos.map((todo) => (
                  <li
                    key={todo.id}
                    className={`bg-white border rounded ${
                      inboxReorder.dragId && inboxReorder.overId === todo.id
                        ? "border-neutral-900"
                        : ""
                    } ${inboxReorder.dragId === todo.id ? "opacity-50" : ""}`}
                    {...(inboxReorder.enabled ? inboxReorder.rowProps(todo) : {})}
                  >
                    <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                      {inboxReorder.enabled && <DragHandle {...inboxReorder.handleProps(todo)} />}
                      <CompleteCheckbox
                        checked={todo.done}
                        onToggle={() => updateTodo(todo, { done: !todo.done })}
                      />
                      <EditableTodo todo={todo} onEdit={editTodo} className="text-sm" />
                      {todo.timesScheduled >= 2 && !todo.done && (
                        <DeferredBadge
                          timesScheduled={todo.timesScheduled}
                          onReset={() => resetDeferred(todo)}
                        />
                      )}
                      {isDueSoon(todo, today) && <DueLabel todo={todo} today={today} />}
                      {!todo.scheduledDate && (
                        <button
                          onClick={() => scheduleToday(todo)}
                          className="shrink-0 text-xs text-neutral-400"
                        >
                          today
                        </button>
                      )}
                      <button
                        onClick={() =>
                          setAddingChildTo(addingChildTo === todo.id ? null : todo.id)
                        }
                        className="shrink-0 text-xs text-neutral-400"
                      >
                        break down
                      </button>
                      <TodoMenu
                        todo={todo}
                        moveTargets={eligibleParentsFor(todo)}
                        onMove={reparentTodo}
                        onSetDue={setDueDate}
                        onSetContext={setContext}
                        onSetRepeat={setRepeat}
                        onDelete={deleteTodo}
                      />
                    </div>
                    {addingChildTo === todo.id && (
                      <form
                        onSubmit={(e) => addChild(todo, e)}
                        className="flex gap-2 px-3 pb-2"
                      >
                        <TitleInput
                          className="border rounded px-2 py-1 text-sm"
                          placeholder="First step…"
                          value={childDrafts[todo.id] || ""}
                          onChange={(v) => setChildDrafts((d) => ({ ...d, [todo.id]: v }))}
                          autoFocus
                        />
                        <button className="text-sm bg-neutral-100 rounded px-2">Add</button>
                      </form>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {parents.map((parent) => {
            const allChildren = childrenOf(parent.id);
            const openChildren = allChildren
              .filter((c) => !c.done)
              .sort(stepCompare);
            const doneChildren = allChildren.filter((c) => c.done).sort(byDoneAtDesc);
            const hiddenOpen = hiddenOpenStepsOf(parent.id);
            const collapsed = collapsedParents[parent.id];
            return (
              <section key={parent.id}>
                <div className="flex flex-wrap items-center justify-between mb-2 gap-2">
                  <div className="flex items-center gap-1 flex-1">
                    <button
                      onClick={() => toggleCollapsed(parent.id)}
                      className="text-xs text-neutral-500 shrink-0"
                    >
                      {collapsed ? "▸" : "▾"}
                    </button>
                    <EditableTodo
                      todo={parent}
                      onEdit={editTodo}
                      className="text-sm font-semibold text-neutral-500"
                    />
                    {parent.timesScheduled >= 2 && !parent.done && (
                      <DeferredBadge
                        timesScheduled={parent.timesScheduled}
                        onReset={() => resetDeferred(parent)}
                      />
                    )}
                  </div>
                  <label className="text-xs text-neutral-400 shrink-0">
                    Steps:{" "}
                    <select
                      value={stepSort}
                      onChange={(e) => setStepSortPersisted(e.target.value as StepSort)}
                      className="bg-transparent border-none"
                    >
                      {(Object.keys(STEP_SORTS) as StepSort[]).map((key) => (
                        <option key={key} value={key}>
                          {STEP_SORTS[key].label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {/* With every open step hidden on this tab, "work on this today"
                      would fall back to scheduling the project itself, so hold it. */}
                  {(openChildren.length > 0 || hiddenOpen === 0) && (
                    <button
                      onClick={() => scheduleTopOf(parent)}
                      className="text-xs text-neutral-500 underline shrink-0"
                    >
                      Work on this today
                    </button>
                  )}
                  <ShareControls project={parent} />
                  {/* Context only: deleting a parent here would orphan its steps. */}
                  <TodoMenu
                    todo={parent}
                    moveTargets={[]}
                    onSetContext={setContext}
                    moveStepsTo={moveStepsTargetFor(parent)}
                    onMoveSteps={moveAllSteps}
                    movingSteps={movingSteps.has(parent.id)}
                  />
                </div>
                {!collapsed && (
                  <>
                    <TodoList
                      todos={openChildren}
                      onToggle={(t) => updateTodo(t, { done: !t.done })}
                      onDelete={deleteTodo}
                      onSchedule={scheduleToday}
                      onEdit={editTodo}
                      getMoveTargets={moveTargetsFor}
                      onMove={reparentTodo}
                      onSetDue={setDueDate}
                      onSetContext={setContext}
                      onSetRepeat={setRepeat}
                      onReorder={stepSort === "manual" ? reorderTodo : undefined}
                      today={today}
                      empty={
                        hiddenOpen > 0
                          ? `${hiddenOpen} open step${hiddenOpen === 1 ? "" : "s"} in ${otherContext}.`
                          : "Nothing here yet."
                      }
                    />
                    {doneChildren.length > 0 && (
                      <div className="mt-1">
                        <button
                          onClick={() => toggleDoneChildrenVisible(parent.id)}
                          className="text-xs text-neutral-400"
                        >
                          {expandedDoneChildren[parent.id] ? "Hide" : "Show"}{" "}
                          {doneChildren.length} completed
                        </button>
                        {expandedDoneChildren[parent.id] && (
                          <TodoList
                            todos={doneChildren}
                            onToggle={(t) => updateTodo(t, { done: !t.done })}
                            onDelete={deleteTodo}
                            onEdit={editTodo}
                            onSetContext={setContext}
                            onSetRepeat={setRepeat}
                            empty=""
                          />
                        )}
                      </div>
                    )}
                    <form onSubmit={(e) => addChild(parent, e)} className="flex gap-2 mt-2">
                      <TitleInput
                        className="border rounded px-2 py-1 text-sm"
                        placeholder="Add a step…"
                        value={childDrafts[parent.id] || ""}
                        onChange={(v) => setChildDrafts((d) => ({ ...d, [parent.id]: v }))}
                      />
                      <button className="text-sm bg-neutral-100 rounded px-2">Add</button>
                    </form>
                  </>
                )}
              </section>
            );
          })}

          <section>
            <button
              onClick={() => setShowCompleted((s) => !s)}
              className="text-sm font-semibold text-neutral-500"
            >
              <span className="text-xs">{showCompleted ? "▾" : "▸"}</span> Completed (
              {completedTopLevel.length})
            </button>
            {showCompleted && (
              <div className="mt-2">
                {!completedTopLevel.length ? (
                  <p className="text-sm text-neutral-400">Nothing completed yet.</p>
                ) : (
                  <ul className="space-y-1">
                    {completedTopLevel.map((item) => {
                      const children = childrenOf(item.id);
                      return (
                        <li key={item.id} className="bg-white border rounded">
                          <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                            <input
                              type="checkbox"
                              checked={item.done}
                              onChange={() => updateTodo(item, { done: !item.done })}
                            />
                            <EditableTodo
                              todo={item}
                              onEdit={editTodo}
                              className="text-sm line-through text-neutral-400"
                            />
                            {item.doneAt && (
                              <span className="shrink-0 text-xs text-neutral-400">
                                {formatDate(item.doneAt)}
                              </span>
                            )}
                            {children.length > 0 && (
                              <button
                                onClick={() => toggleCompletedChildrenVisible(item.id)}
                                className="shrink-0 text-xs text-neutral-400"
                              >
                                {expandedCompletedChildren[item.id] ? "hide" : "show"} steps
                              </button>
                            )}
                            <TodoMenu
                              todo={item}
                              moveTargets={[]}
                              onSetContext={setContext}
                              moveStepsTo={moveStepsTargetFor(item)}
                              onMoveSteps={moveAllSteps}
                              movingSteps={movingSteps.has(item.id)}
                              onDelete={deleteTodo}
                            />
                          </div>
                          {expandedCompletedChildren[item.id] && children.length > 0 && (
                            <div className="px-3 pb-2">
                              <TodoList
                                todos={children}
                                onToggle={(t) => updateTodo(t, { done: !t.done })}
                                onDelete={deleteTodo}
                                onEdit={editTodo}
                                onSetContext={setContext}
                                onSetRepeat={setRepeat}
                                empty=""
                              />
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </section>
        </>
      )}
      </div>

      <FeedbackWidget />
    </div>
    </div>
  );
};

// "deferred N×" badge; clickable to dismiss when onReset is given.
const DeferredBadge = ({
  timesScheduled,
  onReset,
}: {
  timesScheduled: number;
  onReset?: () => void;
}) => {
  const className = "shrink-0 text-xs bg-amber-100 text-amber-700 rounded px-1.5 py-0.5";
  if (!onReset) {
    return (
      <span className={className}>
        deferred {timesScheduled}×
      </span>
    );
  }
  return (
    <button
      onClick={onReset}
      title="Dismiss deferred badge"
      aria-label="Dismiss deferred badge"
      className={className}
    >
      deferred {timesScheduled}× <span aria-hidden="true">✕</span>
    </button>
  );
};

type TodoEditUpdates = { title: string; priority: number | null; complexity: number | null };

const TodoList = ({
  todos,
  onToggle,
  onDelete,
  onSchedule,
  onUnschedule,
  onEdit,
  onReset,
  getParentTitle,
  getContextTag,
  getMoveTargets,
  onMove,
  onSetDue,
  onSetContext,
  onSetRepeat,
  hasSteps,
  onReorder,
  today,
  empty,
}: {
  todos: TodoData[];
  onToggle: (todo: TodoData) => void;
  onDelete: (todo: TodoData) => void;
  onSchedule?: (todo: TodoData) => void;
  onUnschedule?: (todo: TodoData) => void;
  onEdit: (todo: TodoData, updates: TodoEditUpdates) => void;
  onReset?: (todo: TodoData) => void;
  getParentTitle?: (todo: TodoData) => string | undefined;
  getContextTag?: (todo: TodoData) => string | undefined;
  getMoveTargets?: (todo: TodoData) => TodoData[];
  onMove?: (todo: TodoData, parentId: string | null) => void;
  onSetDue?: (todo: TodoData, dueDate: string | null) => void;
  onSetContext?: (todo: TodoData, context: TodoContext) => void;
  onSetRepeat?: (todo: TodoData, repeat: TodoRepeat | null) => void;
  // Lists that can hold a project header (Today, Needs attention, search)
  // pass this so those rows get no Repeat, as on the project's own header:
  // a reset unchecks only the todo, never its steps. A repeat set before the
  // steps were added stays editable, so it can still be turned off. Those
  // rows get no Delete either: deleting a project doesn't take its steps
  // with it, so it would orphan them.
  hasSteps?: (todo: TodoData) => boolean;
  onReorder?: (todo: TodoData, order: number) => void;
  today?: string;
  empty: string;
}) => {
  const reorder = useReorder(todos, onReorder);
  if (!todos.length) {
    return <p className="text-sm text-neutral-400">{empty}</p>;
  }
  return (
    <ul className="space-y-1">
      {todos.map((todo) => {
        const parentTitle = getParentTitle?.(todo);
        const contextTag = getContextTag?.(todo);
        // An item can be scheduled but overdue (rolled over from a past
        // date, still shown wherever it already lived) — "bump" it back to
        // literal today.
        const isOverdue = !!(today && todo.scheduledDate && todo.scheduledDate < today);
        const moveTargets = onMove && !todo.done ? (getMoveTargets?.(todo) ?? []) : [];
        const canSchedule = onSchedule && !todo.done && (!todo.scheduledDate || isOverdue);
        return (
          <li
            key={todo.id}
            className={`flex flex-wrap items-center gap-2 bg-white border rounded px-3 py-2 ${
              reorder.dragId && reorder.overId === todo.id ? "border-neutral-900" : ""
            } ${reorder.dragId === todo.id ? "opacity-50" : ""}`}
            {...(reorder.enabled ? reorder.rowProps(todo) : {})}
          >
            {reorder.enabled && <DragHandle {...reorder.handleProps(todo)} />}
            <CompleteCheckbox checked={todo.done} onToggle={() => onToggle(todo)} />
            <EditableTodo
              todo={todo}
              onEdit={onEdit}
              className={`text-sm ${todo.done ? "line-through text-neutral-400" : ""}`}
            />
            {contextTag && (
              <span className="shrink-0 text-xs bg-neutral-100 text-neutral-500 rounded px-1.5 py-0.5 capitalize">
                {contextTag}
              </span>
            )}
            {parentTitle && (
              <span className="shrink-0 text-xs bg-neutral-100 text-neutral-500 rounded px-1.5 py-0.5">
                in {parentTitle}
              </span>
            )}
            {todo.timesScheduled >= 2 && (
              <DeferredBadge
                timesScheduled={todo.timesScheduled}
                onReset={onReset && (() => onReset(todo))}
              />
            )}
            {today && isDueSoon(todo, today) && <DueLabel todo={todo} today={today} />}
            {todo.done && todo.doneAt && (
              <span className="shrink-0 text-xs text-neutral-400">{formatDate(todo.doneAt)}</span>
            )}
            {canSchedule && (
              <button
                onClick={() => onSchedule!(todo)}
                className="shrink-0 text-xs text-neutral-400"
              >
                {todo.scheduledDate ? "still today" : "today"}
              </button>
            )}
            {onUnschedule && !todo.done && todo.scheduledDate && (
              <button
                onClick={() => onUnschedule(todo)}
                className="shrink-0 text-xs text-neutral-400"
              >
                remove
              </button>
            )}
            <TodoMenu
              todo={todo}
              moveTargets={moveTargets}
              onMove={onMove}
              onSetDue={onSetDue}
              onSetContext={onSetContext}
              onSetRepeat={hasSteps?.(todo) && !todo.repeat ? undefined : onSetRepeat}
              onDelete={hasSteps?.(todo) ? undefined : onDelete}
            />
          </li>
        );
      })}
    </ul>
  );
};

const DueLabel = ({ todo, today }: { todo: TodoData; today: string }) => {
  const label = formatDueDate(todo.dueDate!);
  if (!label) return null;
  return (
    <span
      className={`shrink-0 text-xs ${todo.dueDate! < today ? "text-red-600" : "text-neutral-500"}`}
    >
      Due {label}
    </span>
  );
};

// Secondary header controls (text size, motion) behind one button. The controls stay
// mounted while closed (hidden via the `hidden` class; the `hidden` attribute loses to `.flex`) because FontSizeControl applies the saved text
// size from an effect on mount. On mobile the wrapper is not `relative`, so the popover
// anchors to the viewport (inset-x-4, wraps) and can't overflow the header's wrapped row; from sm up it
// hangs off the button (right-0). Closes on Escape (useFocusTrap) and outside pointerdown.
// Also the discoverable way into the keyboard-shortcuts list ("?" is the fast way),
// kept here rather than as another header button because the header row is crowded.
const HeaderSettings = ({ onShowShortcuts }: { onShowShortcuts: () => void }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const close = () => setOpen(false);
  useFocusTrap(popRef, open, close);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);
  return (
    <span ref={wrapRef} className="sm:relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Settings"
        title="Settings"
        className="min-h-10 min-w-10 sm:min-h-0 sm:min-w-0 px-2 py-1 rounded bg-neutral-100 text-sm text-neutral-700"
      >
        ⋯
      </button>
      <div
        ref={popRef}
        role="dialog"
        aria-label="Settings"
        tabIndex={-1}
        className={`${open ? "flex" : "hidden"} absolute inset-x-4 z-10 mt-1 flex-wrap items-center justify-end gap-x-3 gap-y-2 sm:inset-x-auto sm:right-0 sm:flex-nowrap sm:justify-start rounded border border-gray-200 bg-white p-2 shadow`}
      >
        <FontSizeControl />
        <MotionControl />
        <button
          onClick={() => {
            close();
            onShowShortcuts();
          }}
          aria-keyshortcuts="?"
          title="Keyboard shortcuts (?)"
          className="min-h-10 sm:min-h-0 whitespace-nowrap text-sm text-neutral-500"
        >
          Shortcuts
        </button>
      </div>
    </span>
  );
};

// The "move to…" option that un-nests a step into the Inbox. Ids are safe ids
// (letters, digits, hyphens), so the leading colon can't collide with one.
const INBOX_MOVE_TARGET = ":inbox";

// Per-todo menu: Move to + Due date + Context + Repeat (+ Move all steps) + Delete. Closes on
// Escape (useFocusTrap, which also restores focus to the button) and on outside
// pointerdown (works for touch).
// Context (work/home) lives here rather than on the row because switching it is
// rare, per the feedback that asked for it to stay out of the way.
const TodoMenu = ({
  todo,
  moveTargets,
  onMove,
  onSetDue,
  onSetContext,
  onSetRepeat,
  moveStepsTo,
  onMoveSteps,
  movingSteps,
  onDelete,
}: {
  todo: TodoData;
  moveTargets: TodoData[];
  // A null parentId means "to the Inbox" (top level).
  onMove?: (todo: TodoData, parentId: string | null) => void;
  onSetDue?: (todo: TodoData, dueDate: string | null) => void;
  onSetContext?: (todo: TodoData, context: TodoContext) => void;
  // null turns repeating off.
  onSetRepeat?: (todo: TodoData, repeat: TodoRepeat | null) => void;
  // "Move all steps to {moveStepsTo}": a separate action for projects, kept
  // apart from the Context switch above so switching a project never cascades
  // on its own. Shown only when both are given (i.e. the todo has steps).
  moveStepsTo?: TodoContext;
  onMoveSteps?: (todo: TodoData, context: TodoContext) => void;
  // True while this todo's "Move all steps" run is in flight: a cue shows
  // beside the ⋯ button (the click closed the menu, so there's nothing else
  // to see) and the item itself is disabled on reopen.
  movingSteps?: boolean;
  onDelete?: (todo: TodoData) => void;
}) => {
  const canMoveSteps = !!onMoveSteps && !!moveStepsTo;
  // A step can always go back to the Inbox — unless it's scheduled, when it
  // would land in Today instead and the label would lie; "remove" it from
  // Today first.
  const canMoveToInbox = !!todo.parentId && !todo.scheduledDate;
  const canMove = !!onMove && (moveTargets.length > 0 || canMoveToInbox);
  const canSetDue = !!onSetDue && !todo.done;

  const [open, setOpen] = useState(false);
  // Repeat is edited as a draft and saved with one PATCH (not one per
  // keystroke). The menu stays mounted while closed, so the draft is reset
  // from the stored value each time it opens.
  const [repeatEvery, setRepeatEvery] = useState(String(todo.repeat?.every ?? 1));
  const [repeatUnit, setRepeatUnit] = useState<RepeatUnit | "">(todo.repeat?.unit ?? "");
  // Declared after the two above: Dashboard.context.test.mjs's react stub
  // hands queued useState values out in call order.
  const [repeatFromDraft, setRepeatFromDraft] = useState<RepeatFrom>(repeatFrom(todo.repeat));
  // The stored anchor when there is one, so reopening doesn't re-anchor;
  // today otherwise.
  const [repeatStart, setRepeatStart] = useState(todo.repeat?.anchor ?? ymdOf(new Date()));
  const repeatDraft = parseRepeatDraft(repeatEvery, repeatUnit, repeatFromDraft, repeatStart);
  const repeatChanged = repeatDraft !== undefined && !sameRepeat(todo.repeat, repeatDraft);
  const saveRepeat = () => {
    if (!onSetRepeat || !repeatChanged) return;
    onSetRepeat(todo, repeatDraft);
    close();
  };
  const wrapRef = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const close = () => setOpen(false);
  useFocusTrap(popRef, open, close);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);
  // Render only when at least one item is available.
  if (!canMove && !canSetDue && !onSetRepeat && !onSetContext && !canMoveSteps && !onDelete) return null;
  return (
    <span ref={wrapRef} className="relative shrink-0 ml-auto sm:ml-0">
      {/* Always mounted so screen readers announce the text when it appears
          (a live region inserted with text already in it often isn't read). */}
      {canMoveSteps && (
        <span aria-live="polite" className="text-xs text-neutral-400">
          {movingSteps ? "Moving steps…" : ""}
        </span>
      )}
      <button
        onClick={() => {
          if (!open) {
            setRepeatEvery(String(todo.repeat?.every ?? 1));
            setRepeatUnit(todo.repeat?.unit ?? "");
            setRepeatFromDraft(repeatFrom(todo.repeat));
            setRepeatStart(todo.repeat?.anchor ?? ymdOf(new Date()));
          }
          setOpen(!open);
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Todo options"
        className="px-3 py-2 min-h-10 -my-2 sm:my-0 sm:min-h-0 sm:px-2 sm:py-1 text-xs text-neutral-600 hover:text-neutral-900"
      >
        ⋯
      </button>
      {open && (
        <div
          aria-hidden="true"
          onClick={close}
          className="fixed inset-0 z-40 bg-black/30 sm:hidden"
        />
      )}
      {open && (
        <div
          ref={popRef}
          role="dialog"
          aria-label="Todo options"
          tabIndex={-1}
          className="fixed inset-x-0 bottom-0 z-50 max-h-[80vh] overflow-y-auto rounded-t-xl border-t-2 border-neutral-400 shadow-[0_-8px_24px_rgba(0,0,0,0.3)] sm:z-10 sm:max-h-none sm:overflow-visible sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-0 sm:mt-1 sm:w-56 sm:rounded sm:border sm:border-gray-200 sm:shadow divide-y divide-neutral-200 bg-white p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:pb-2 pl-[max(0.5rem,env(safe-area-inset-left))] pr-[max(0.5rem,env(safe-area-inset-right))] sm:pl-2 sm:pr-2"
        >
          <div className="truncate pb-2 text-sm sm:hidden">{todo.title}</div>
          {canMove && (
            <div className="pb-2">
              <select
                value=""
                onChange={(e) => {
                  const target = e.target.value;
                  if (target) {
                    onMove!(todo, target === INBOX_MOVE_TARGET ? null : target);
                    close();
                  }
                }}
                className="min-h-10 sm:min-h-0 w-full text-xs text-neutral-600 bg-transparent border rounded"
              >
                <option value="">move to…</option>
                {canMoveToInbox && <option value={INBOX_MOVE_TARGET}>Inbox</option>}
                {moveTargets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          {canSetDue && (
            <div className="flex items-center gap-2 py-2 text-xs text-neutral-600">
              <label className="flex items-center gap-2">
                Due date
                <input
                  type="date"
                  value={todo.dueDate ?? ""}
                  onChange={(e) => onSetDue!(todo, e.target.value || null)}
                  className="min-h-10 sm:min-h-0 border rounded px-1 text-xs"
                />
              </label>
              {todo.dueDate && (
                <button
                  type="button"
                  aria-label="Clear due date"
                  onClick={() => onSetDue!(todo, null)}
                  className="min-h-10 sm:min-h-0 px-2 sm:px-0 text-neutral-400"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          {onSetContext && (
            <div
              role="group"
              aria-label="Context"
              className="flex items-center gap-2 py-2 text-xs text-neutral-600"
            >
              Context
              <div className="flex rounded overflow-hidden border">
                {([CONTEXTS.WORK, CONTEXTS.HOME] as const).map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-pressed={todo.context === c}
                    onClick={() => {
                      // Re-picking the current context is a no-op: no write.
                      if (todo.context === c) return;
                      onSetContext(todo, c);
                      close();
                    }}
                    className={`min-h-10 sm:min-h-0 px-2 py-1 text-xs capitalize ${
                      todo.context === c
                        ? "bg-neutral-900 text-white"
                        : "bg-neutral-100 text-neutral-700"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
          {onSetRepeat && (
            <form
              role="group"
              aria-label="Repeat"
              onSubmit={(e) => {
                e.preventDefault();
                saveRepeat();
              }}
              className="flex flex-wrap items-center gap-2 py-2 text-xs text-neutral-600"
            >
              Repeat
              <span className="rounded bg-amber-100 px-1 text-[10px] uppercase tracking-wide text-amber-800">
                Beta
              </span>
              {repeatUnit !== "" && (
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={MAX_REPEAT_EVERY}
                  aria-label="Repeat every"
                  value={repeatEvery}
                  onChange={(e) => setRepeatEvery(e.target.value)}
                  className="min-h-10 sm:min-h-0 w-14 border rounded px-1 text-xs"
                />
              )}
              <select
                aria-label="Repeat unit"
                value={repeatUnit}
                onChange={(e) => {
                  setRepeatUnit(e.target.value as RepeatUnit | "");
                  if (!repeatEvery.trim()) setRepeatEvery("1");
                }}
                className="min-h-10 sm:min-h-0 text-xs text-neutral-600 bg-transparent border rounded"
              >
                <option value="">Off</option>
                <option value="day">days</option>
                <option value="week">weeks</option>
                <option value="month">months</option>
              </select>
              {repeatUnit !== "" && (
                <select
                  aria-label="Repeat counts from"
                  value={repeatFromDraft}
                  onChange={(e) => setRepeatFromDraft(e.target.value as RepeatFrom)}
                  className="min-h-10 sm:min-h-0 text-xs text-neutral-600 bg-transparent border rounded"
                >
                  <option value="done">After I check it off</option>
                  <option value="start">Starting on a date</option>
                </select>
              )}
              {repeatUnit !== "" && repeatFromDraft === "start" && (
                <input
                  type="date"
                  aria-label="Repeat start date"
                  value={repeatStart}
                  onChange={(e) => setRepeatStart(e.target.value)}
                  className="min-h-10 sm:min-h-0 border rounded px-1 text-xs"
                />
              )}
              {repeatDraft === undefined && (
                <span className="text-neutral-400">
                  {repeatFromDraft === "start" && !repeatStart ? "Pick a date" : `1–${MAX_REPEAT_EVERY}`}
                </span>
              )}
              {repeatChanged && (
                <button
                  type="submit"
                  className="min-h-10 sm:min-h-0 px-2 sm:px-0 text-neutral-900 underline"
                >
                  Save
                </button>
              )}
              {todo.repeat && (
                <button
                  type="button"
                  onClick={() => {
                    onSetRepeat(todo, null);
                    close();
                  }}
                  className="min-h-10 sm:min-h-0 px-2 sm:px-0 text-neutral-500 underline"
                >
                  Remove repeat
                </button>
              )}
            </form>
          )}
          {canMoveSteps && (
            <div className="py-2">
              <button
                type="button"
                disabled={!!movingSteps}
                onClick={() => {
                  onMoveSteps!(todo, moveStepsTo!);
                  close();
                }}
                className="min-h-10 sm:min-h-0 w-full text-left text-xs text-neutral-600 hover:text-neutral-900 disabled:text-neutral-400 disabled:hover:text-neutral-400"
              >
                {movingSteps ? "Moving steps…" : `Move all steps to ${moveStepsTo}`}
              </button>
            </div>
          )}
          {onDelete && (
            <div className="mt-4 pt-2">
              <button
                type="button"
                onClick={() => {
                  onDelete(todo);
                  close();
                }}
                className="min-h-10 sm:min-h-0 w-full text-left text-xs text-red-600 hover:text-red-700"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      )}
    </span>
  );
};

const PRIORITY_LABELS: Record<number, string> = { 1: "P1", 2: "P2", 3: "P3" };
const COMPLEXITY_LABELS: Record<number, string> = { 1: "S", 2: "M", 3: "L" };

// Shared by every place a title shows (todos, projects/parents alike) — an
// inline rename+refine with explicit save/cancel rather than relying on
// blur/Enter, which get ambiguous about double-firing a save.
const EditableTodo = ({
  todo,
  onEdit,
  className,
}: {
  todo: TodoData;
  onEdit: (todo: TodoData, updates: TodoEditUpdates) => void;
  className?: string;
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(todo.title);
  const [priority, setPriority] = useState<number | undefined>(todo.priority);
  const [complexity, setComplexity] = useState<number | undefined>(todo.complexity);

  const startEditing = () => {
    setValue(todo.title);
    setPriority(todo.priority);
    setComplexity(todo.complexity);
    setEditing(true);
  };

  if (editing) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = value.trim();
          if (!trimmed) return;
          onEdit(todo, {
            title: trimmed,
            priority: priority ?? null,
            complexity: complexity ?? null,
          });
          setEditing(false);
        }}
        className="flex-1 flex flex-wrap items-center gap-1 min-w-[16rem]"
      >
        <TitleInput
          className="text-sm border rounded px-1"
          minWidthClass="min-w-[8rem]"
          value={value}
          onChange={setValue}
          autoFocus
        />
        <select
          value={priority ?? ""}
          onChange={(e) => setPriority(e.target.value ? Number(e.target.value) : undefined)}
          className="min-h-10 sm:min-h-0 text-xs border rounded shrink-0"
        >
          <option value="">P</option>
          <option value="1">P1</option>
          <option value="2">P2</option>
          <option value="3">P3</option>
        </select>
        <select
          value={complexity ?? ""}
          onChange={(e) => setComplexity(e.target.value ? Number(e.target.value) : undefined)}
          className="min-h-10 sm:min-h-0 text-xs border rounded shrink-0"
        >
          <option value="">C</option>
          <option value="1">S</option>
          <option value="2">M</option>
          <option value="3">L</option>
        </select>
        <button type="submit" className="min-h-10 sm:min-h-0 -my-2 sm:my-0 px-2 sm:px-0 text-xs text-neutral-500 shrink-0">
          save
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="min-h-10 sm:min-h-0 -my-2 sm:my-0 px-2 sm:px-0 text-xs text-neutral-400 shrink-0"
        >
          cancel
        </button>
      </form>
    );
  }

  return (
    <div className="flex-1 flex items-center gap-1 min-w-[9rem] basis-0">
      <span
        className={`min-w-0 break-words sm:truncate sm:min-w-[3rem] sm:max-w-[14rem] ${className || ""}`}
      >
        {todo.title}
      </span>
      {todo.priority && (
        <span className="text-xs bg-neutral-100 text-neutral-500 rounded px-1 py-0.5 shrink-0">
          {PRIORITY_LABELS[todo.priority]}
        </span>
      )}
      {todo.complexity && (
        <span className="text-xs bg-neutral-100 text-neutral-500 rounded px-1 py-0.5 shrink-0">
          {COMPLEXITY_LABELS[todo.complexity]}
        </span>
      )}
      {todo.repeat && (
        <span
          title={describeRepeat(todo.repeat)}
          className="text-xs bg-neutral-100 text-neutral-500 rounded px-1 py-0.5 shrink-0 whitespace-nowrap"
        >
          <span aria-hidden="true">{formatRepeatShort(todo.repeat)}</span>
          <span className="sr-only">{describeRepeat(todo.repeat)}</span>
        </span>
      )}
      <button
        onClick={startEditing}
        className="min-h-10 sm:min-h-0 -my-2 sm:my-0 px-2 sm:px-0 text-xs text-neutral-400 shrink-0"
      >
        edit
      </button>
    </div>
  );
};
